import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toastr } from 'react-redux-toastr';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { AsyncBox } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import {
  Banner,
  Box,
  Button,
  Spinner,
  Stack,
  SubmitButton,
  Tooltip,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  FloppyIcon,
  TrashIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';
import {
  ModalConfirm,
  useConfirmModal,
} from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import {
  SectionedModal,
  useSectionedModal,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import useMiniProfileSections from './useMiniProfileSections';
import { resolveDisabilitySubFieldReset } from './fieldRules';
import buildMiniProfileSavePayload, {
  buildDeleteApplicantPayload,
} from './buildSavePayload';
import usePortalAccountActions from './usePortalAccountActions';
import {
  isSendInviteBlockedByPrimary,
  buildApplicantPortalAccess,
} from './portalActions';
import {
  DEFAULT_PHONE_TYPE,
  DEFAULT_EMAIL_TYPE,
} from './additionalContactFields';
// eslint-disable-next-line max-len
import useHouseholdMemberDeleteEligibilityQuery from '../../../hooks/data-fetching/useHouseholdMemberDeleteEligibilityQuery';
import useHouseholdOpenCertsQuery from '../../../hooks/data-fetching/useHouseholdOpenCertsQuery';
import useOpenCertsLockInfoQuery from '../../../hooks/data-fetching/useOpenCertsLockInfoQuery';
// eslint-disable-next-line max-len
import useHouseholdWillReopenCertification from '../../../hooks/data-fetching/useHouseholdWillReopenCertification';
import useHouseholdMembersQuery from '../../../hooks/data-fetching/useHouseholdMembersQuery';
import useHouseholdPendingMembersQuery from '../../../hooks/data-fetching/useHouseholdPendingMembersQuery';
import useResidentQuery from '../../../hooks/data-fetching/useResidentQuery';
import { applyDoNotWishToProvideRule } from '../../../hooks/utils/useMultiRaceSelect';
import { MULTI_RACE_FIELD } from './demographicFields';
import useCustomerPortalStatusQuery from '../../../hooks/data-fetching/useCustomerPortalStatusQuery';
import usePrimaryPortalStatusQuery from '../../../hooks/data-fetching/usePrimaryPortalStatusQuery';
import useMiniProfileSubject from '../../../hooks/data-fetching/useMiniProfileSubject';
import { invalidateAffordableQualificationQueries } from '../../GeneralAffordable/hooks';
import invalidateOccupantInfoQueries from '../../../hooks/data-fetching/invalidateOccupantInfoQueries';
import useResidentMutation from '../../../hooks/data-modifying/useResidentMutation';
import useApplicantMutation from '../../../hooks/data-modifying/useApplicantMutation';
import useCreateHouseholdMemberMutation from '../../../hooks/data-modifying/useCreateHouseholdMemberMutation';
// eslint-disable-next-line max-len
import useCreateApplicantHouseholdMemberMutation from '../../../hooks/data-modifying/useCreateApplicantHouseholdMemberMutation';
import useDeleteHouseholdMemberMutation from '../../../hooks/data-modifying/useDeleteHouseholdMemberMutation';
import usePermissionsHook from '../../../components/ElementWithPermissions/usePermissionsHook';
import useIntl from '../../../hooks/useIntl';
import appMessages from '../../App/messages';
import queryClient from '../../../react-query-client';
import { extractErrorMessage } from '../../../utils/errors';
import { AppContext } from '../../App/context';
import { useEditHouseholdMemberDatesEnabled } from '../hooks';
import messages from '../messages';
import bridgeMessages from './messages';
// Imported, not duplicated -- this is the exact same warning
// EditApplicationHousehold's legacy handleSubmit shows, and the two must
// never be able to drift apart into different wording for the same event.
import editApplicationHouseholdMessages from '../../EditApplicationHousehold/messages';

// The open-resync effect below waits for an in-flight occupantInfo
// refetch to settle before resyncing the form to it. If the user starts
// interacting (typing, clicking Add Phone Number/Add Email Address)
// during that wait, applying the resync once the fetch finally settles
// would silently discard what they just did. Exported so this decision is
// unit-testable without needing to exercise the effect's own timing.
export const shouldSkipOpenResync = ({
  isDirty,
  revealedAdditionalPhoneCount,
  revealedAdditionalEmailCount,
}) =>
  Boolean(isDirty) ||
  revealedAdditionalPhoneCount !== null ||
  revealedAdditionalEmailCount !== null;

// Lives in its own module so the Certification tab can use it without pulling
// in this whole container; re-exported to keep the existing import path.
export { invalidateOccupantInfoQueries };

// Only refetch for a real, existing member while the modal is actually
// open and the underlying queries are even enabled -- see the effect that
// uses this for the full reasoning.
export const shouldRefetchOnOpen = ({
  open,
  isEditingExistingMember,
  isEditHouseholdMemberDatesEnabled,
}) =>
  Boolean(open && isEditingExistingMember && isEditHouseholdMemberDatesEnabled);

// Hidden, not closed, while a confirmation is open — the same convention as
// ChangeHeadOfHousehold. The parent-controlled `open` never changes, so the
// form never unmounts and unsaved edits survive a cancel.
export const isSectionedModalOpen = ({ open, confirmationOpen }) =>
  Boolean(open) && !confirmationOpen;

// Account Management only applies to a Current adult occupant behind the
// flag -- gating the status query on the same condition keeps it from
// firing (real backend load) for every resident-profile view regardless of
// member type/status. customerId is deliberately not re-checked here:
// useCustomerPortalStatusQuery already guards on Boolean(customerId)
// internally, so this predicate only owns the flag/type/status half.
export const isPortalStatusQueryEnabled = ({
  isEditHouseholdMemberDatesEnabled,
  isResidentPortalActive,
  isApplicantSubject = false,
  occupantInfo,
}) =>
  Boolean(isEditHouseholdMemberDatesEnabled) &&
  // The legacy panel hides the whole portal surface when the property has
  // Resident Portal switched off; without this a Current adult there would
  // fetch a status nobody can act on, and be offered an invite to a portal
  // the property does not run.
  Boolean(isResidentPortalActive) &&
  occupantInfo?.type === 'adult' &&
  // An applicant is always Pending; its own Customer record is the live member to act on.
  (occupantInfo?.status === 'Current' || isApplicantSubject);

// Stays mounted between opens, so refetchOnMount never fires and a stale status would show.
// Exported: useEffect callbacks don't run under this file's shallow rendering.
export const shouldRefetchPortalQueryOnOpen = ({
  open,
  isApplicantSubject,
  isQueryEnabled,
}) => Boolean(open) && Boolean(isApplicantSubject) && Boolean(isQueryEnabled);

// The Collapse+`hidden` wrapper around a Secondary/Tertiary field only
// animates it out of view -- it does not clear the underlying
// react-hook-form value (confirmed by testing: filling a field, clicking
// its trash icon, then clicking Add again showed the old value right
// back). Clicking Remove calls these explicitly to actually blank it --
// Type resets to the same Mobile/Personal default a never-touched slot
// gets (useMiniProfileSections's getDefaultValues), not to blank, so
// Remove-then-Add looks identical to a fresh Add. Exported (setValue
// passed in, not read from a ref) so the field-name mapping is
// unit-testable directly.
export const applyAdditionalPhoneSlotClear = (setValue, slot) => {
  const prefix = slot === 'secondary' ? 'secondaryPhone' : 'tertiaryPhone';
  setValue(`generalInformation.${prefix}.phoneNumber`, '');
  setValue(`generalInformation.${prefix}.phoneType`, DEFAULT_PHONE_TYPE);
};
export const applyAdditionalEmailSlotClear = (setValue, slot) => {
  const prefix = slot === 'secondary' ? 'secondaryEmail' : 'tertiaryEmail';
  setValue(`generalInformation.${prefix}.emailAddress`, '');
  setValue(`generalInformation.${prefix}.emailType`, DEFAULT_EMAIL_TYPE);
};

// The Form library drops an onChange set on a field config, so the rule the
// other multi-race editors run in onChange is applied from the watch
// subscription instead. It only writes back when the rule removed something,
// so the write doesn't re-trigger itself.
export const applyMultiRaceSelectionRule = (setValue, selectedRaces) => {
  const allowedRaces = applyDoNotWishToProvideRule(selectedRaces);
  if (allowedRaces.length === selectedRaces.length) return;
  setValue(MULTI_RACE_FIELD, allowedRaces, { shouldDirty: true });
};

// Shown for Current/Prior members, always — not conditioned on whether any
// conditionally-required field is incomplete.
const CONDITIONALLY_REQUIRED_BANNER_MESSAGE =
  'This icon highlights information that may be required by other features or processes.';
// Forced to 'no' when "Disabled" flips to 'no', cleared on 'notWishToProvide'
// — same dependency getDisabilityValues enforces on the application form.
// Left alone (not reset) when "Disabled" flips back to 'yes'; they're
// simply re-enabled at whatever value they're already holding.
const DISABILITY_SUB_FIELD_NAMES = [
  'affordableInformation.disability.disabilityMobile',
  'affordableInformation.disability.disabilityHearing',
  'affordableInformation.disability.disabilityVisual',
  'affordableInformation.disability.disabilityOther',
];

// Clearing a saved identification (SSN/Alien/Passport/License) blocks Save,
// unlike every other conditionally-required field. Exempt when the user cannot
// satisfy it: Personal Information is read-only for a Pending Applicant, while
// creating a member, and under an open certification (isPersonalCertLocked).
// Exported so the rule is pinned directly: dropping any exemption silently
// makes the relevant flow unsaveable, with nothing else to catch it.
export const isSaveBlockedByMissingIdentification = ({
  isPending,
  isAdding,
  isAdult,
  isAffordable = false,
  isCommercial = false,
  certLock,
  hasAtLeastOneIdentification,
  hadIdentificationOnLoad,
}) => {
  // A commercial tenant has no Personal Information section at all, so
  // there is nothing the user could fill in to satisfy this.
  if (
    isCommercial ||
    isPending ||
    isAdding ||
    certLock ||
    hasAtLeastOneIdentification
  ) {
    return false;
  }
  // A conventional minor is exempt outright: the application form never asks
  // one for an SSN or Alien Registration, and no affordable program consumes
  // it, so the value is the Mini-Profile's alone -- including removing it.
  if (!isAdult && !isAffordable) {
    return false;
  }
  // Only a saved identification is protected; a member that never had one
  // must still be savable (e.g. ending a former member's residency).
  return hadIdentificationOnLoad;
};

// Declared once, at module level, so usePermissionsHook's useMemo (keyed on
// this array) isn't re-run on every render.
const RESIDENT_DELETE_SCOPE = ['resident-delete'];

// Module-level for the same reason as RESIDENT_DELETE_SCOPE: a fresh array
// would re-run usePermissionsHook's useMemo every render.
const START_END_DATE_SCOPE = ['resident-edit-start-end-dates'];

// Keyed on the blockedReason values the delete-eligibility endpoint answers
// with -- each string is word-for-word the error the DELETE would have been
// rejected with, so the tooltip and a toast can never disagree.
const DELETE_BLOCKED_REASON_MESSAGES = {
  HEAD_OF_HOUSEHOLD: bridgeMessages.deleteHouseholdMemberPrimaryTooltip,
  OPEN_CERTIFICATION: bridgeMessages.deleteHouseholdMemberOpenCertTooltip,
  COMPLETED_CERTIFICATION:
    bridgeMessages.deleteHouseholdMemberCompletedCertTooltip,
};

// Head of household and open certification are already known locally (the
// same two sources useMiniProfileSections derives endDateDisabled from), so
// they answer without waiting on the network; the third block -- the member
// appearing on a completed certification -- is only knowable from the
// endpoint. Order matches the precedence the backend rejects in, so whichever
// reason is shown is the one the DELETE would have given. Exported so that
// precedence is unit-testable on its own.
export const resolveDeleteBlockedReason = ({
  isPrimary,
  householdHasOpenCerts,
  blockedReason,
}) => {
  if (isPrimary) return 'HEAD_OF_HOUSEHOLD';
  if (householdHasOpenCerts) return 'OPEN_CERTIFICATION';
  return blockedReason ?? null;
};

// SectionedModal renders actionsProps itself, into a right-aligned Stack of
// plain Buttons: nothing can sit on the left there, and a Button built that
// way has nowhere to hang the Tooltip explaining why Delete is disabled.
// `actions` replaces that footer whole, so it is owned here instead. Cancel
// and Save still come from an array, mapped with the same children-keyed
// variant/icon defaults the library applies, so they look untouched.
export const MiniProfileModalActions = ({
  deleteActionProps = null,
  actionsProps = [],
}) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="center"
    gap={2}
  >
    {/* Always rendered, even empty: space-between with a single child would
        pull Cancel/Save to the left. */}
    <Box>
      {deleteActionProps ? (
        // The span is load-bearing: a disabled MUI Button sets
        // pointer-events: none, so the Tooltip never sees the hover unless an
        // enabled element wraps it. An empty title renders no tooltip at all.
        <Tooltip title={deleteActionProps.tooltip ?? ''} placement="top">
          <span>
            <Button
              variant="deleteSubtle"
              startIcon={<TrashIcon />}
              disabled={deleteActionProps.disabled}
              onClick={deleteActionProps.onClick}
              data-testid={deleteActionProps['data-testid']}
            >
              {deleteActionProps.children}
            </Button>
          </span>
        </Tooltip>
      ) : null}
    </Box>
    <Stack direction="row" gap={2}>
      {actionsProps.map(
        ({ children, variant, startIcon, submitButton, ...rest }) => {
          // submitButton picks the component, exactly as the library's own
          // mapper does -- swallowing it here would both lose the
          // SubmitButton and leak an unknown attribute onto the DOM node.
          // Variant and icon are read from the entry rather than inferred from
          // its label, which is translated at runtime: the library's own mapper
          // compares against the English strings, so Cancel would fall through
          // to the primary variant in any other locale.
          const ActionButton = submitButton ? SubmitButton : Button;
          return (
            <ActionButton
              key={rest['data-testid']}
              startIcon={startIcon}
              variant={variant ?? 'primary'}
              {...rest}
            >
              {children}
            </ActionButton>
          );
        },
      )}
    </Stack>
  </Stack>
);

// react-hook-form's useForm({defaultValues}) (inside the component library's
// useSectionedModal) only reads defaultValues on the FIRST render of this
// component instance — it does not reset when defaultValues changes later.
// MiniProfileBridgeContent must therefore only ever mount once its
// occupantInfo is the real, final record; MiniProfileBridge (below)
// is the gate that holds it unmounted until the React Query data has loaded,
// and remounts it (via `key`) whenever a different member is selected.
export const MiniProfileBridgeContent = ({
  open,
  onClose,
  title,
  occupantInfo,
  organizationId,
  propertyId,
  residentId,
  isResidentPortalActive,
  suffixes,
  relationshipOptions,
  minorRelationshipOptions,
  applicantTypeOptions,
  agedUpMinorApplicantTypeId,
  // Option triples for the two applicant types that force a fixed Relation +
  // Affordable Relation -- read by useMiniProfileSections's Type cascade.
  guarantorOptions,
  liveInCaretakerOptions,
  states,
  countries,
  affordableRelationships,
  specialNeedsDesignations,
  isAffordable,
  isCommercial = false,
  householdHasOpenCerts,
  hasLockedOpenCert = false,
  lockInfoPending = false,
  // The household moved out, not the member -- exempts it from the open-cert
  // field lock, since the open cert may be a historical one started precisely
  // to correct this data.
  isPriorResident = false,
  householdMoveInDate,
  householdMoveOutDate,
  annualIncome,
  onSave,
  isSaving,
  isFetchingFreshOccupantInfo = false,
  // ENG-30290/D2 -- gates the 4 new Secondary/Tertiary Phone/Email fields;
  // see useMiniProfileSections.js for the actual rendering/defaultValues
  // logic this only threads through to.
  isEditHouseholdMemberDatesEnabled = false,
  canDeleteHouseholdMember = false,
  canEditStartEndDates = true,
  onDeleteHouseholdMember,
  deleteBlockedReason = null,
  isDeleteEligibilityUnknown = false,
  // Affordable Relation must be enabled+required while adding an adult
  // through the applicant path, unlike the resident Add flow, which defers
  // it to the application and must keep its current disabled+optional
  // behavior untouched. Its own prop rather than folding into isPending/
  // isAdding -- neither of those means "applicant subject", and repurposing
  // one would also flip this field's behavior for the resident Add flow.
  isApplicantSubject = false,
  // Applicant path only: together these gate Send Portal Invite behind the Head of Household.
  applicationId = null,
  isAffordaPortal = false,
  // Applicant path only: the application page's Redux panels only refresh if the caller is told.
  onPortalActionComplete,
  // Hides the form while the parent's add confirmation is up -- hidden, not
  // closed, so a cancel returns to the filled form with its input intact.
  addConfirmationOpen = false,
}) => {
  const { formatMessage } = useIntl();
  const isPending = occupantInfo?.status === 'Pending';
  const isAdult = occupantInfo?.type === 'adult';
  // True for the duration of creating a brand-new member — it has no id until
  // the first Save. The Mini-Profile closes on that Save, so this never flips
  // to false while open: the created member is seen on the next reopen, by
  // which point it's an ordinary existing member.
  const isAdding = !occupantInfo?.id;
  // The same occupantInfo.isPrimary useMiniProfileSections derives
  // endDateDisabled from.
  const isPrimary = Boolean(occupantInfo?.isPrimary);
  // The identification-group icon/helper text (Driver's License/SSN/
  // Passport/Alien Registration) must clear live as the user types/selects,
  // not only after Save+reopen. useMiniProfileSections can't watch the form
  // itself (it builds the sections config the form doesn't exist from yet
  // — see the comment above), so the live values are watched here, off the
  // ReactHookFormProps this component already gets from useSectionedModal,
  // and threaded back in.
  const [livePersonalInformation, setLivePersonalInformation] = useState(null);
  // Same live-watch story as above, for "Disabled" (see
  // DISABILITY_SUB_FIELD_NAMES) — Mobile/Hearing/Visual/Other Disability
  // must disable (and Mobile/Hearing/Visual/Other reset to 'no') the moment
  // the user picks 'no', not only after Save+reopen.
  const [liveHasDisability, setLiveHasDisability] = useState(null);
  // Same live-watch story as livePersonalInformation above — lets
  // hasClearedRequiredField (below) see a previously-saved Gender/Veteran/
  // Race/Ethnicity/Affordable Relation get cleared as it happens, not only
  // after Save+reopen.
  const [liveAffordableInformation, setLiveAffordableInformation] =
    useState(null);
  // Same live-watch story again, for Type -- the cascade must lock Relation
  // and Affordable Relation the moment a cascaded type is picked, not only
  // after Save+reopen.
  const [liveApplicantTypeId, setLiveApplicantTypeId] = useState(null);
  // ENG-30290 -- how many of the 2 additional Phone/Email slots the user has
  // revealed via "Add" this session. null means "nothing clicked yet" --
  // useMiniProfileSections then falls back to however many the loaded record
  // already has, exactly like the three live values above fall back to
  // defaultValues. Reset to null at the same two points those three are
  // (close, and open-resync below), for the same reason: this component
  // isn't remounted between one member and the next reopening of the SAME
  // member (only key={occupantInfo?.id} below causes that, and only for a
  // DIFFERENT member), so without an explicit reset this would otherwise
  // keep showing whatever was revealed last time, even after the underlying
  // record changed (e.g. a member whose additions were removed and saved).
  const [revealedAdditionalPhoneCount, setRevealedAdditionalPhoneCount] =
    useState(null);
  const [revealedAdditionalEmailCount, setRevealedAdditionalEmailCount] =
    useState(null);
  const isPortalStatusEnabled = isPortalStatusQueryEnabled({
    isEditHouseholdMemberDatesEnabled,
    isResidentPortalActive,
    isApplicantSubject,
    occupantInfo,
  });
  const portalStatusQuery = useCustomerPortalStatusQuery({
    organizationId,
    propertyId,
    customerId: occupantInfo?.customerId,
    options: { enabled: isPortalStatusEnabled },
  });

  // Only where the Head-of-Household rule applies; no extra request elsewhere.
  const isPrimaryStatusEnabled =
    isApplicantSubject &&
    isAffordaPortal &&
    !occupantInfo?.isPrimary &&
    occupantInfo?.type === 'adult' &&
    Boolean(applicationId);
  const {
    data: primaryStatus,
    // isFetching, not isLoading: a reopen refetches over warm cache, where isLoading is false.
    isFetching: isPrimaryStatusPending,
    refetch: refetchPrimaryStatus,
  } = usePrimaryPortalStatusQuery(applicationId, {
    enabled: isPrimaryStatusEnabled,
  });
  const sendInviteBlockedByPrimary = isSendInviteBlockedByPrimary({
    isApplicantSubject,
    isAffordaPortal,
    isPrimary: Boolean(occupantInfo?.isPrimary),
    isPrimaryStatusPending,
    primaryStatus,
  });

  // Reset toggle, Block/Unblock and Reset Login Attempts change the account
  // state and last-login/invite timestamps read elsewhere in this modal --
  // both need to be current the moment the action completes, not on the
  // next reopen.
  // Memoized because it is handed to usePortalAccountActions, whose
  // onActionChange useMiniProfileSections depends on as a stable leaf of its
  // sections memo.
  const { refetch: refetchPortalStatus } = portalStatusQuery;
  const refreshAfterPortalAction = useCallback(async () => {
    await Promise.all([
      refetchPortalStatus(),
      // Nothing resident-scoped to invalidate on the applicant path.
      ...(isApplicantSubject
        ? [onPortalActionComplete?.()]
        : [
            invalidateOccupantInfoQueries(queryClient, {
              organizationId,
              propertyId,
              residentId,
            }),
          ]),
    ]);
  }, [
    refetchPortalStatus,
    organizationId,
    propertyId,
    residentId,
    isApplicantSubject,
    onPortalActionComplete,
  ]);

  useEffect(() => {
    if (
      shouldRefetchPortalQueryOnOpen({
        open,
        isApplicantSubject,
        isQueryEnabled: isPortalStatusEnabled,
      })
    ) {
      refetchPortalStatus();
    }
    // refetch() ignores `enabled`, so guard it here. The Head of Household can finish setup
    // while this modal is closed; without the refetch the block keeps the first open's answer.
    if (
      shouldRefetchPortalQueryOnOpen({
        open,
        isApplicantSubject,
        isQueryEnabled: isPrimaryStatusEnabled,
      })
    ) {
      refetchPrimaryStatus();
    }
  }, [
    open,
    isApplicantSubject,
    isPortalStatusEnabled,
    isPrimaryStatusEnabled,
    refetchPortalStatus,
    refetchPrimaryStatus,
  ]);

  // portalStatusQuery.data is passed as-is (not `?? {}`) -- the hook's own
  // default parameter already handles undefined with a stable module-level
  // object, so doing it here too would just reintroduce the same fresh-
  // object-per-render churn on every call where the query hasn't resolved
  // yet.
  const portalAccountActions = usePortalAccountActions({
    organizationId,
    propertyId,
    customerId: occupantInfo?.customerId,
    // The applicant record has no portalUser; branched so the resident path reads the same field as before.
    portalUserId: isApplicantSubject
      ? portalStatusQuery.data?.portalUser?.id
      : occupantInfo?.portalUser?.id,
    portalStatus: portalStatusQuery.data,
    // The status-derived banners are meaningless until the request has
    // actually answered -- an empty status reads as "no email, no account".
    hasPortalStatus: portalStatusQuery.isSuccess,
    portalStatusFailed: portalStatusQuery.isError,
    memberStatus: occupantInfo?.status,
    endDate: occupantInfo?.endDate,
    onActionComplete: refreshAfterPortalAction,
    isApplicantSubject,
    sendInviteBlockedByPrimary,
  });

  // ReactHookFormProps doesn't exist yet at this point in the render (it
  // comes from useSectionedModal below, which itself needs `sections` --
  // built by useMiniProfileSections, called before it) -- clicking Remove
  // only needs setValue once the click actually happens, well after this
  // render finishes, so a ref sidesteps the ordering problem: read
  // .current inside the callback, write it once ReactHookFormProps exists
  // further down.
  const reactHookFormPropsRef = useRef(null);
  const clearAdditionalPhoneSlot = useCallback((slot) => {
    const setValue = reactHookFormPropsRef.current?.setValue;
    if (setValue) applyAdditionalPhoneSlotClear(setValue, slot);
  }, []);
  const clearAdditionalEmailSlot = useCallback((slot) => {
    const setValue = reactHookFormPropsRef.current?.setValue;
    if (setValue) applyAdditionalEmailSlotClear(setValue, slot);
  }, []);
  const {
    defaultValues,
    sections,
    hasAtLeastOneIdentification,
    hadIdentificationOnLoad,
    hasClearedRequiredField,
    isPersonalCertLocked,
  } = useMiniProfileSections({
    occupantInfo,
    suffixes,
    relationshipOptions,
    minorRelationshipOptions,
    applicantTypeOptions,
    agedUpMinorApplicantTypeId,
    states,
    countries,
    affordableRelationships,
    specialNeedsDesignations,
    isAffordable,
    isCommercial,
    isPending,
    isAdding,
    isApplicantSubject,
    householdHasOpenCerts,
    hasLockedOpenCert,
    lockInfoPending,
    isPriorResident,
    householdMoveInDate,
    householdMoveOutDate,
    annualIncome,
    livePersonalInformation,
    liveHasDisability,
    liveAffordableInformation,
    liveApplicantTypeId,
    guarantorOptions,
    liveInCaretakerOptions,
    isEditHouseholdMemberDatesEnabled,
    canEditStartEndDates,
    revealedAdditionalPhoneCount,
    setRevealedAdditionalPhoneCount,
    revealedAdditionalEmailCount,
    setRevealedAdditionalEmailCount,
    clearAdditionalPhoneSlot,
    clearAdditionalEmailSlot,
    // The member-status half of the gate stays in useMiniProfileSections --
    // duplicating it here would only let the two drift. The property setting
    // is a different axis and only reachable from this component, so it is
    // applied by withholding the hook entirely, which that gate already
    // treats as "no Account Management".
    portalAccountActions: isResidentPortalActive ? portalAccountActions : null,
    portalAccess: isApplicantSubject
      ? buildApplicantPortalAccess(portalStatusQuery.data)
      : null,
  });
  const { ReactHookFormProps, sections: resolvedSections } = useSectionedModal({
    sections,
    defaultValues,
  });
  reactHookFormPropsRef.current = ReactHookFormProps;

  useEffect(() => {
    const subscription = ReactHookFormProps.watch((values, { name }) => {
      if (name === MULTI_RACE_FIELD) {
        applyMultiRaceSelectionRule(
          ReactHookFormProps.setValue,
          values?.affordableInformation?.multiRace ?? [],
        );
      }
      if (!name || name.startsWith('personalInformation.')) {
        setLivePersonalInformation(values?.personalInformation ?? null);
      }
      if (!name || name.startsWith('affordableInformation.')) {
        setLiveAffordableInformation(values?.affordableInformation ?? null);
      }
      if (!name || name === 'affordableInformation.disability.hasDisability') {
        setLiveHasDisability(
          values?.affordableInformation?.disability?.hasDisability ?? null,
        );
      }
      if (!name || name === 'generalInformation.applicantTypeId') {
        setLiveApplicantTypeId(
          values?.generalInformation?.applicantTypeId ?? null,
        );
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ReactHookFormProps.watch]);

  // The disabling itself is a derived render (useMiniProfileSections, above)
  // — this is only the value side effect, and only fires on an actual
  // transition (liveHasDisability starts null and only updates via the watch
  // subscription above, which itself only fires on real user-driven changes,
  // never on mount). See resolveDisabilitySubFieldReset for the values.
  useEffect(() => {
    const resetValue = resolveDisabilitySubFieldReset(liveHasDisability);
    if (resetValue === null) return;
    DISABILITY_SUB_FIELD_NAMES.forEach((name) => {
      ReactHookFormProps.setValue(name, resetValue);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveHasDisability]);

  // Same member reopened without a remount (the `key` above only changes
  // for a different member) reuses this same RHF instance, so any unsaved
  // edits/clears from the last time it was open would otherwise still be
  // sitting in the form. Discard them back to the loaded record on every
  // close (Cancel, backdrop click, Esc all call onClose).
  useEffect(() => {
    if (open) return;
    ReactHookFormProps.reset(defaultValues);
    setLivePersonalInformation(null);
    setLiveHasDisability(null);
    setLiveAffordableInformation(null);
    setLiveApplicantTypeId(null);
    setRevealedAdditionalPhoneCount(null);
    setRevealedAdditionalEmailCount(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // react-hook-form's defaultValues are read only once, at this instance's
  // original mount -- opening again later doesn't re-read them on its own.
  // That original mount can be arbitrarily stale by the time it's reopened
  // (e.g. this SPA came back from the browser's back/forward cache and its
  // React Query caches are still mid-refetch right as the user reopens).
  // Wait for that in-flight fetch to actually settle, then resync once —
  // "once" matters just as much as "wait": a LATER, unrelated background
  // refetch (e.g. plain window-focus refetching) firing while the user is
  // mid-edit must not wipe their in-progress changes out from under them.
  const hasResyncedOnOpenRef = useRef(false);
  useEffect(() => {
    if (!open) {
      hasResyncedOnOpenRef.current = false;
      return;
    }
    if (isFetchingFreshOccupantInfo || hasResyncedOnOpenRef.current) return;
    hasResyncedOnOpenRef.current = true;
    if (
      shouldSkipOpenResync({
        isDirty: ReactHookFormProps.isDirty,
        revealedAdditionalPhoneCount,
        revealedAdditionalEmailCount,
      })
    ) {
      return;
    }
    ReactHookFormProps.reset(defaultValues);
    setLivePersonalInformation(null);
    setLiveHasDisability(null);
    setLiveAffordableInformation(null);
    setLiveApplicantTypeId(null);
    setRevealedAdditionalPhoneCount(null);
    setRevealedAdditionalEmailCount(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isFetchingFreshOccupantInfo]);

  // Creating a member can Save too, so there is no "Add is unsupported" gate
  // here any more: whether the form is submittable is driven entirely by
  // field-level validity, surfaced the same way as any other validation
  // failure, via ReactHookFormProps.isSubmitDisabled.
  const actionsProps = [
    {
      children: formatMessage(appMessages.cancel),
      variant: 'text',
      onClick: onClose,
      'data-testid': 'mini-profile-cancel-button',
    },
    {
      // minWidth avoids the button shrinking to the spinner's width;
      // darkMode keeps the spinner visible against this button's background.
      children: isSaving ? <Spinner small darkMode /> : 'Save',
      variant: 'primary',
      startIcon: isSaving ? undefined : <FloppyIcon />,
      sx: { minWidth: 88 },
      onClick: ReactHookFormProps.handleSubmit(onSave),
      // The existing helper text, already shown on every field in that state,
      // is the only explanation product wanted for either gate below — no
      // separate error message. See isSaveBlockedByMissingIdentification for
      // the identification group.
      //
      // The second gate: clearing a previously-saved value in one of the other
      // conditionally-required fields (Date of Birth, Marital Status, Gender,
      // Veteran, Disabled, Race, Ethnicity, Affordable Relation) also blocks
      // Save outright, with the same Pending exception. A member being created
      // can't trip it — its defaults are blank, so nothing can have been
      // cleared (wasFieldCleared in useMiniProfileSections).

      disabled:
        ReactHookFormProps.isSubmitDisabled ||
        isSaving ||
        isSaveBlockedByMissingIdentification({
          isPending,
          isAdding,
          isAdult,
          isAffordable,
          isCommercial,
          certLock: isPersonalCertLocked,
          hasAtLeastOneIdentification,
          hadIdentificationOnLoad,
        }) ||
        (!isPending && hasClearedRequiredField),
      'data-testid': 'mini-profile-save-button',
    },
  ];

  // Applicant-stage delete goes through the applicants endpoint, which
  // only rejects a Head of Household -- no open/completed-certification
  // block exists there (a completed cert is reopened, not blocked; ENG-30354's
  // delete-eligibility endpoint isn't called for this subject at all, see
  // MiniProfileBridge below). The resident path keeps every block as before.
  const deleteBlockedMessage = isApplicantSubject
    ? isPrimary
      ? DELETE_BLOCKED_REASON_MESSAGES.HEAD_OF_HOUSEHOLD
      : null
    : DELETE_BLOCKED_REASON_MESSAGES[
        resolveDeleteBlockedReason({
          isPrimary,
          householdHasOpenCerts,
          blockedReason: deleteBlockedReason,
        })
      ] ?? null;
  // Never offered while a member is being created -- there is nothing
  // persisted to delete until the first Save. A Pending Applicant is offered
  // it: it has no Resident row, but the applicant-scoped route the container
  // picks for it deletes the Applicant side directly.
  const deleteActionProps =
    canDeleteHouseholdMember && !isAdding
      ? {
          children: formatMessage(bridgeMessages.deleteHouseholdMember),
          tooltip: deleteBlockedMessage
            ? formatMessage(deleteBlockedMessage)
            : '',
          // Kept disabled, reasonless, until the eligibility answer lands:
          // one of the three blocks is invisible from here, so offering the
          // button before then risks a click the backend can only reject. A
          // failed read leaves it enabled, degrading to that rejection rather
          // than a permanently dead button.
          disabled: Boolean(deleteBlockedMessage) || isDeleteEligibilityUnknown,
          onClick: onDeleteHouseholdMember,
          'data-testid': 'mini-profile-delete-button',
        }
      : null;

  const headerBannerMessage = isPending
    ? formatMessage(bridgeMessages.pendingApplicantBanner)
    : CONDITIONALLY_REQUIRED_BANNER_MESSAGE;

  return (
    <>
      <SectionedModal
        open={isSectionedModalOpen({
          open,
          confirmationOpen:
            portalAccountActions.confirmation.open || addConfirmationOpen,
        })}
        title={title}
        onClose={onClose}
        sections={resolvedSections}
        ReactHookFormProps={ReactHookFormProps}
        actions={
          <MiniProfileModalActions
            deleteActionProps={deleteActionProps}
            actionsProps={actionsProps}
          />
        }
        // The banner describes the conditionally-required icon; no commercial
        // field carries that icon, so the banner would point at nothing.
        headerBanner={
          isCommercial
            ? undefined
            : { status: 'warning', message: headerBannerMessage }
        }
      />
      <ModalConfirm
        open={portalAccountActions.confirmation.open}
        onClose={portalAccountActions.confirmation.onCancel}
        title={portalAccountActions.confirmation.title}
        actionsProps={[
          {
            children: formatMessage(appMessages.cancel),
            variant: 'text',
            onClick: portalAccountActions.confirmation.onCancel,
          },
          {
            children: portalAccountActions.confirmation.confirmLabel,
            onClick: portalAccountActions.confirmation.onConfirm,
          },
        ]}
      >
        <Typography variant="bodyMedium">
          {portalAccountActions.confirmation.body}
        </Typography>
      </ModalConfirm>
    </>
  );
};

export const MiniProfileBridge = ({
  open,
  onClose,
  title,
  occupantInfo,
  residentId,
  // Applicant-stage twin of residentId: { kind: 'applicant', applicationId,
  // householdId }. Absent (the default) or { kind: 'resident' } leaves every
  // read/write below on the exact same resident-scoped path this component
  // has always used -- see useMiniProfileSubject's own top-of-file comment
  // for why the resident branch isn't also routed through it. `applicants`
  // is `application.applicants` (Redux, already loaded by ApplicationProfile)
  // -- the raw list useMiniProfileSubject reshapes into household members.
  subject,
  applicants,
  // getOneApplication's in-flight window, so the open-resync below can wait for
  // it the way the resident path waits on React Query.
  isFetchingApplicants = false,
  // Whether a household change would reopen a certification here --
  // read by handleSave (applicant-subject Add only) to decide whether
  // adding a member needs the same warning EditApplicationHousehold shows
  // before a household-composition change reopens one. Defaulted false so
  // the resident path, which never passes this, is untouched. Optional
  // override, mainly for tests -- the real signal is sourced below.
  willReopenCertification: willReopenCertificationProp = false,
  // The household's affordable programs (ApplicationProfile only, from
  // affordableTabs/hudTab) -- drives useHouseholdWillReopenCertification
  // below. ResidentProfile never passes this, which is what keeps that
  // query off on the resident path.
  affordableProgramIds = [],
  suffixes,
  relationshipOptions,
  minorRelationshipOptions,
  applicantTypeOptions,
  agedUpMinorApplicantTypeId,
  guarantorOptions,
  liveInCaretakerOptions,
  states,
  countries,
  affordableRelationships,
  specialNeedsDesignations,
  isAffordable,
  // Resident-side only: ApplicationProfile never passes it, and an applicant
  // household is never a prior-resident one, so the false default is right.
  isPriorResident = false,
  // Application-level (Application.isCommercial), never the property's
  // hasCommercialFloorPlans -- a mixed property has commercial tenants too.
  isCommercial = false,
  onSaveSuccess,
}) => {
  const { userOrganizationId, selectedProperty } = useContext(AppContext) ?? {};
  const organizationId = userOrganizationId;
  const propertyId = selectedProperty?.id;
  const intl = useIntl();
  const formatMessage = intl.formatMessage;
  // MiniProfileBridge itself is always mounted (ResidentProfile/index.js
  // just toggles `open`), so without this every one of the queries below
  // would fire for every resident-profile view regardless of the flag —
  // real backend load for users who can never actually open this modal.
  const isEditHouseholdMemberDatesEnabled =
    useEditHouseholdMemberDatesEnabled();
  const isApplicantSubject = subject?.kind === 'applicant';
  const flags = useFlags();
  // The Head-of-Household gate below only exists on AffordaPortal.
  const isAffordaPortal = Boolean(flags?.affordaPortaV1);
  // Every resident-scoped query below stays gated exactly as before
  // (isEditHouseholdMemberDatesEnabled alone) for every existing caller,
  // which never passes `subject` -- the extra `&& !isApplicantSubject` only
  // ever turns them off in addition, never on, and only for the new
  // applicant-subject path, which reads through useMiniProfileSubject below
  // instead.
  const queryOptions = {
    enabled: isEditHouseholdMemberDatesEnabled && !isApplicantSubject,
  };
  // Sourced from the same per-program query the Completed tab renders from
  // (see the hook's own comment), not a value hand-supplied by
  // ApplicationProfile -- gated the same way applicantSubject below is, so
  // the resident path issues no query.
  const queryWillReopenCertification = useHouseholdWillReopenCertification({
    organizationId,
    propertyId,
    householdId: subject?.householdId,
    propertyAffordableProgramIds: affordableProgramIds,
    enabled: isEditHouseholdMemberDatesEnabled && isApplicantSubject,
  });
  const willReopenCertification =
    willReopenCertificationProp || queryWillReopenCertification;
  const hasResidentDeletePermission = usePermissionsHook({
    scope: RESIDENT_DELETE_SCOPE,
  });
  const canDeleteHouseholdMember =
    isEditHouseholdMemberDatesEnabled && hasResidentDeletePermission;
  const hasStartEndDatePermission = usePermissionsHook({
    scope: START_END_DATE_SCOPE,
  });
  // Flag off means the permission is not consulted at all.
  const canEditStartEndDates =
    !isEditHouseholdMemberDatesEnabled || hasStartEndDatePermission;

  // Don't trust the Redux-derived occupantInfo.residentInfo for the form's
  // actual data — it can silently resolve empty. Re-fetch fresh via React
  // Query and use that record instead whenever it has the member;
  // occupantInfo (Redux) is only used to identify *which* member to look up
  // and as a fallback shape for the "Add" flow, which has no id to look up.
  const isEditingExistingMember = Boolean(occupantInfo?.id);
  const {
    data: currentHouseholdMembers,
    isLoading: isLoadingCurrentMembers,
    isFetching: isFetchingCurrentMembers,
  } = useHouseholdMembersQuery({
    organizationId,
    propertyId,
    residentId,
    options: queryOptions,
  });
  const {
    data: pendingHouseholdMembers,
    isLoading: isLoadingPendingMembers,
    isFetching: isFetchingPendingMembers,
  } = useHouseholdPendingMembersQuery({
    organizationId,
    propertyId,
    residentId,
    options: queryOptions,
  });
  // The applicant-shaped twin of the two resident queries above --
  // application.applicants is already loaded (Redux) by the time this ever
  // mounts, so there is no separate fetch/loading state to gate on for that
  // half; only the household-scoped open-certs read inside it is a real
  // network call.
  const applicantSubject = useMiniProfileSubject({
    subject,
    organizationId,
    propertyId,
    applicants,
    options: {
      enabled: isEditHouseholdMemberDatesEnabled && isApplicantSubject,
    },
  });
  const isLoadingFreshOccupantInfo = isApplicantSubject
    ? false
    : isEditingExistingMember &&
      (isLoadingCurrentMembers || isLoadingPendingMembers);
  // Distinct from isLoadingFreshOccupantInfo above (only true before the
  // very first fetch ever completes) — this stays accurate for every later
  // background refetch too (e.g. the invalidation that fires once this SPA
  // comes back from the browser's back/forward cache), which is exactly
  // when MiniProfileBridgeContent's already-open-once form most needs to
  // know not to trust defaultValues yet.
  const isFetchingFreshOccupantInfo = isApplicantSubject
    ? isFetchingApplicants
    : isEditingExistingMember &&
      (isFetchingCurrentMembers || isFetchingPendingMembers);

  // Forces a fresh fetch every time the modal opens, rather than only
  // reacting to whatever background fetch happens to already be under way
  // for some other reason (a save's own invalidation, or an unrelated
  // window-focus refetch). Confirmed by testing: reopening the same member
  // right after clicking a Secondary/Tertiary trash icon and then
  // Cancelling instead of saving also showed it blank -- whatever the
  // client did to the form during the last open must never be what
  // determines what shows up on the next one; only the server's current
  // state should. Gated the same way the underlying queries themselves
  // already are (isEditHouseholdMemberDatesEnabled) -- invalidating a
  // disabled query is a harmless no-op, but there is no reason to even try
  // while this whole freshness mechanism is off. The guard is exported so
  // it's unit-testable -- useEffect callbacks don't fire under this file's
  // Enzyme shallow rendering.
  useEffect(() => {
    // Redux-backed: no React Query cache to invalidate. ApplicationProfile
    // re-dispatches getOneApplication on open instead.
    if (isApplicantSubject) return;
    if (
      !shouldRefetchOnOpen({
        open,
        isEditingExistingMember,
        isEditHouseholdMemberDatesEnabled,
      })
    ) {
      return;
    }
    invalidateOccupantInfoQueries(queryClient, {
      organizationId,
      propertyId,
      residentId,
    });
  }, [
    open,
    isApplicantSubject,
    isEditingExistingMember,
    isEditHouseholdMemberDatesEnabled,
    organizationId,
    propertyId,
    residentId,
  ]);

  const freshOccupantInfo = useMemo(() => {
    if (!isEditingExistingMember) return occupantInfo;
    const allMembers = isApplicantSubject
      ? applicantSubject.members
      : [
          ...(currentHouseholdMembers ?? []),
          ...(pendingHouseholdMembers ?? []),
        ];
    return (
      allMembers.find((member) => member.id === occupantInfo.id) ?? occupantInfo
    );
  }, [
    occupantInfo,
    isEditingExistingMember,
    isApplicantSubject,
    applicantSubject.members,
    currentHouseholdMembers,
    pendingHouseholdMembers,
  ]);

  // Blocks Start/End Date edits while the household has an open
  // certification — same has-open-certs endpoint EditResidentHousehold
  // already uses to gate the legacy household-edit page. Applicant subject
  // reads this household-scoped instead (useMiniProfileSubject), since a
  // Pending household has no Resident row to key the resident-scoped read
  // off of.
  const { data: householdHasOpenCertsResident } = useHouseholdOpenCertsQuery({
    organizationId,
    propertyId,
    residentId,
    options: queryOptions,
  });
  const householdHasOpenCerts = isApplicantSubject
    ? applicantSubject.householdHasOpenCerts
    : householdHasOpenCertsResident;

  // queryOptions already disables this for an applicant subject (a Pending
  // household never cert-locks, and the endpoint is resident-scoped); with
  // no open certs the result is never read, so skip the request too.
  const { data: openCertsLockInfo, isSuccess: lockInfoResolved } =
    useOpenCertsLockInfoQuery({
      organizationId,
      propertyId,
      residentId,
      options: {
        ...queryOptions,
        enabled: queryOptions.enabled && Boolean(householdHasOpenCertsResident),
      },
    });
  const hasLockedOpenCert = Boolean(openCertsLockInfo?.hasLockedOpenCert);
  // Unresolved lock info fails closed downstream; only a successful fetch
  // unlocks. `data === undefined` would not be enough: a failed refetch
  // keeps the last successful data with status 'error', so a stale
  // hasLockedOpenCert: false could outlive the approval it predates.
  const lockInfoPending = !lockInfoResolved;

  // Same query useHouseholdMembersQuery/useHouseholdPendingMembersQuery
  // already fetch internally — React Query dedupes by key, so this doesn't
  // trigger a second network request. householdInfo.householdUnits is a
  // hasMany association but HouseholdUnit.householdId is unique, so at
  // most one row is ever returned.
  const { data: residentQueryData } = useResidentQuery({
    organizationId,
    propertyId,
    residentId,
    options: queryOptions,
  });
  // Neither a move-in date nor a move-out date exists pre-move-in -- an
  // application-stage household has no lease/householdUnit to read either
  // off of.
  const householdMoveInDate = isApplicantSubject
    ? null
    : residentQueryData?.householdInfo?.householdMoveInDate ?? null;
  const householdMoveOutDate = isApplicantSubject
    ? null
    : residentQueryData?.householdInfo?.householdUnits?.[0]?.moveOutDate ??
      null;
  // The delete endpoint is household-scoped, not resident-scoped. Applicant
  // subject already carries its own householdId (the id it was opened
  // with) rather than deriving it from a resident-scoped query.
  const householdId = isApplicantSubject
    ? applicantSubject.householdId
    : residentQueryData?.householdInfo?.id ?? null;
  // Applicant.annualIncome — not in the household-members list response, but
  // already present (unfiltered attributes) on every entry of
  // applicantInfo.applicants, since nothing whitelists that association's
  // columns. Matched by id against this member's own applicantId, since the
  // array holds every applicant in the household, not just this resident's.
  const currentApplicant = residentQueryData?.applicantInfo?.applicants?.find(
    (applicant) => applicant.id === freshOccupantInfo?.applicantId,
  );
  // Applicant subject already carries its own annualIncome directly on the
  // member (useMiniProfileSubject.helpers.js, read straight off
  // application.applicants) -- no second resident-detail fetch needed.
  const annualIncome = isApplicantSubject
    ? freshOccupantInfo?.annualIncome ?? null
    : currentApplicant?.annualIncome ?? null;

  // Only asked for once the Delete button is actually rendered -- no
  // permission, flag off, or the Add flow all keep it off, so opening a
  // Mini-Profile that can't delete costs no request.
  //
  // Branched on the same signal handleSave below picks applicantMutation with,
  // so the read, the delete and the save can't disagree about which side of a
  // member exists: a Pending Applicant has no Resident row (created at
  // move-in), so it is addressed by Applicant id. `id` currently holds that
  // same value, but the named field is what actually means it.
  const isPendingMember = freshOccupantInfo?.status === 'Pending';
  const {
    data: deleteEligibility,
    isLoading: isLoadingDeleteEligibility,
    isIdle: isDeleteEligibilityIdle,
  } = useHouseholdMemberDeleteEligibilityQuery({
    organizationId,
    propertyId,
    householdId,
    residentId: isPendingMember ? undefined : freshOccupantInfo?.id,
    applicantId: isPendingMember ? freshOccupantInfo?.applicantId : undefined,
    options: {
      // This endpoint is resident-scoped and 404s for an applicant-stage
      // member (deliberately, by that endpoint's own design) -- the
      // applicant path's only delete block (Head of Household) is already
      // known locally, so it never needs this read.
      enabled:
        canDeleteHouseholdMember &&
        isEditingExistingMember &&
        !isApplicantSubject,
    },
  });
  // Idle counts as unanswered too, not just loading: householdId arrives from
  // the resident query, so the eligibility read can't even start until that
  // settles -- and a delete attempted without it would hit a broken URL. An
  // outright failure is neither, leaving the button offered and the backend to
  // reject it, exactly as before this read existed. Always false for the
  // applicant subject -- the query above is disabled, so it would otherwise
  // stay idle forever and leave Delete permanently disabled.
  const isDeleteEligibilityUnknown =
    !isApplicantSubject &&
    (isLoadingDeleteEligibility || isDeleteEligibilityIdle);

  // Shared by the update, create and delete success paths below. D-fact #3
  // (CONTEXT.md): the legacy EditResidentHousehold saga is, today, the only
  // thing that invalidates these three keys after any household-member
  // change — without doing the same here, this Bridge (React Query-backed)
  // would keep serving stale household/resident/open-certs/assistance data
  // right after a save.
  //
  // Awaited (invalidateQueries' own returned promise resolves once the
  // refetch it triggers actually completes, since MiniProfileBridge keeps
  // these queries mounted/active even while closed) so the cache is
  // genuinely fresh by the time handleSaveSuccess below calls onClose --
  // confirmed by testing: reopening the same member right after saving a
  // Secondary/Tertiary phone/email showed it blank, because onClose used
  // to fire immediately, well before the invalidated refetch had actually
  // finished. Reopening fast enough could then land the open-resync effect
  // (MiniProfileBridgeContent) on a still-stale render, which then marks
  // itself "already resynced" and never revisits it once the real data
  // does arrive a moment later -- a hard page refresh masked this by
  // forcing a fresh fetch from scratch instead of reusing that state.
  const invalidateHouseholdMemberQueries = () =>
    Promise.all([
      // getResidentHousehold + getResident are also exactly what
      // useHouseholdMembersQuery reads, so this refreshes the member list a
      // delete has to drop a row from, not just this modal's own record.
      // Skipped for an applicant subject -- application.applicants is
      // Redux-backed, not React Query, so there is no cache under these
      // resident-scoped keys to invalidate; onSaveSuccess (below) is what
      // refreshes it instead.
      isApplicantSubject
        ? Promise.resolve()
        : invalidateOccupantInfoQueries(queryClient, {
            organizationId,
            propertyId,
            residentId,
          }),
      // Any member save invalidates the household's open certs server-side
      // (qualification test results, set-asides, generated-document flags),
      // so the Qualification Test section must re-read them or it keeps a
      // stale "tested" state with no Test Household button. The helper also
      // covers getCompletionStatusesAndAssistanceDetails: a birthday/age
      // edit (or a brand-new minor's birthday on create), and removing a
      // member outright, change the HUD/RD assistance fingerprint.
      Promise.resolve(invalidateAffordableQualificationQueries(queryClient)),
    ]);

  const invalidateAndNotify = async () => {
    await invalidateHouseholdMemberQueries();
    // The visible Household table still reads from Redux
    // (getHouseholdResidentMembers), not React Query — invalidating the
    // queries above only refreshes this modal's own data if reopened.
    // onSaveSuccess lets the caller also refresh the Redux-backed table.
    onSaveSuccess?.();
    toastr.success(
      formatMessage(messages.miniProfileSaveSuccessTitle),
      formatMessage(messages.miniProfileSaveSuccessMessage),
    );
  };

  // Create and update behave identically on success: the Mini-Profile closes.
  // For a create, the member's name becomes the title, and the remaining
  // sections open up, the next time it's reopened — the reopen reads the
  // now-invalidated queries, so no post-save identity has to be tracked here.
  const handleSaveSuccess = async () => {
    await invalidateAndNotify();
    // Flagged so a parent that refreshes on close (to catch edits made
    // elsewhere while the modal was open) doesn't repeat the two fetches
    // onSaveSuccess just ran.
    onClose({ afterSave: true });
  };

  // residentService.updateResident/applicantService.updateApplicant's
  // request() rejects with parsedResponse.json?.message ?? .error ??
  // 'Request failed (...)' — usually a real, human-readable string (e.g. a
  // Joi validation message), but can resolve to a non-string (e.g. `{}` for
  // a bare 500) when the backend's own error response has no message/error
  // string of its own. Only surface it when it's actually a string;
  // otherwise fall back to the generic message rather than showing
  // "[object Object]".
  const handleSaveError = (error) => {
    const backendMessage = typeof error === 'string' ? error : null;
    toastr.error(
      formatMessage(messages.miniProfileSaveErrorTitle),
      backendMessage || formatMessage(messages.miniProfileSaveErrorMessage),
    );
  };

  const residentMutation = useResidentMutation({
    organizationId,
    propertyId,
    options: { onSuccess: handleSaveSuccess, onError: handleSaveError },
  });

  // A Pending Applicant is saved via Applicants.js#updateApplicant (updates
  // the application), not Residents.js#updateResident — they haven't moved
  // in yet, so there's no Resident row to update.
  const applicantMutation = useApplicantMutation({
    organizationId,
    propertyId,
    options: { onSuccess: handleSaveSuccess, onError: handleSaveError },
  });

  // ENG-30200 AC#3/#4 -- the only mutation of the three that can create a
  // member (see its own file for why it's a separate hook, not a branch
  // inside useResidentMutation/useApplicantMutation).
  const createMutation = useCreateHouseholdMemberMutation({
    organizationId,
    propertyId,
    residentId,
    options: { onSuccess: handleSaveSuccess, onError: handleSaveError },
  });

  // D5 -- the applicant-subject twin of createMutation above, routed through
  // updateApplicants instead of editResidentHousehold so the completed-
  // move-in-certification auto-reopen isn't silently bypassed. Always called
  // (hooks can't be conditional) but only ever mutated when isApplicantSubject.
  const createApplicantMutation = useCreateApplicantHouseholdMemberMutation({
    organizationId,
    propertyId,
    applicationId: subject?.applicationId,
    options: { onSuccess: handleSaveSuccess, onError: handleSaveError },
  });

  const { openConfirm, openConfirmModal, closeConfirmModal } =
    useConfirmModal();
  // Captured on the click rather than read off freshOccupantInfo at confirm
  // time: closing the Mini-Profile below hands control back to the parent,
  // whose selected-member state isn't guaranteed to still hold this member.
  // Which id it is travels with it, so the route can't be chosen from a
  // member other than the one the id came from.
  const [memberPendingDelete, setMemberPendingDelete] = useState(null);
  // Holds the built create payload while the reopen confirmation is up, so
  // Confirm sends exactly what Save built rather than rebuilding it.
  const [pendingCreatePayload, setPendingCreatePayload] = useState(null);

  const handleDeleteClick = () => {
    setMemberPendingDelete(
      isPendingMember
        ? {
            applicantId: freshOccupantInfo?.applicantId ?? null,
            // Only read on the applicant path (buildDeleteApplicantPayload
            // below) -- captured here for the same reason applicantId/
            // residentId are: freshOccupantInfo isn't trustworthy once
            // onClose() below hands selection back to the parent.
            ...(isApplicantSubject ? { type: freshOccupantInfo?.type } : {}),
          }
        : { residentId: freshOccupantInfo?.id ?? null },
    );
    // Deliberately unlike every other confirmation over a form modal here,
    // which leaves the form mounted/open behind so a cancel returns to it:
    // cancelling this one must NOT bring the Mini-Profile back, so the form
    // is closed for real.
    onClose();
    openConfirmModal();
  };

  // No onClose to call, unlike a save's success -- clicking Delete already
  // closed the Mini-Profile.
  const handleDeleteSuccess = async () => {
    closeConfirmModal();
    await invalidateHouseholdMemberQueries();
    onSaveSuccess?.();
    toastr.success(
      formatMessage(appMessages.success),
      formatMessage(bridgeMessages.deleteHouseholdMemberSuccessMessage),
    );
  };

  // Every blocked-delete reason is decided by the backend, so its own message
  // is the only accurate thing to show. Still reachable despite the
  // eligibility read disabling the button up front: the household can change
  // under an open Mini-Profile, and a failed read leaves the button offered.
  // extractErrorMessage falls back to String(error) for a rejection carrying
  // no message of its own, which would read as "[object Object]" -- generic
  // message for those.
  const handleDeleteError = (error) => {
    closeConfirmModal();
    const backendMessage = extractErrorMessage(error);
    const hasBackendMessage =
      typeof error === 'string' || backendMessage !== String(error);
    toastr.error(
      formatMessage(appMessages.error),
      hasBackendMessage
        ? backendMessage
        : formatMessage(bridgeMessages.deleteHouseholdMemberErrorMessage),
    );
  };

  const deleteMutation = useDeleteHouseholdMemberMutation({
    organizationId,
    propertyId,
    householdId,
    options: { onSuccess: handleDeleteSuccess, onError: handleDeleteError },
  });

  // Applicant-stage delete goes through the same PUT .../applicants route
  // the Add flow uses (D5) -- not ENG-30354's household-scoped delete above,
  // which 404s pre-move-in. A second instance of that same hook, bound to
  // this delete's own success/error handlers instead of Save's: react-query
  // calls a mutate()-level callback ADDITIONALLY to the hook-level one, not
  // instead of it, so sharing createApplicantMutation itself would fire both
  // this delete's toast and Save's.
  const deleteApplicantMutation = useCreateApplicantHouseholdMemberMutation({
    organizationId,
    propertyId,
    applicationId: subject?.applicationId,
    options: { onSuccess: handleDeleteSuccess, onError: handleDeleteError },
  });

  const handleDeleteConfirm = () => {
    if (isApplicantSubject) {
      if (!memberPendingDelete?.applicantId) return;
      deleteApplicantMutation.mutate(
        buildDeleteApplicantPayload(memberPendingDelete, { applicants }),
      );
      return;
    }
    const memberId =
      memberPendingDelete?.applicantId ?? memberPendingDelete?.residentId;
    if (!memberId) return;
    deleteMutation.mutate(memberPendingDelete);
  };

  const handleSave = (formValues) => {
    const payload = buildMiniProfileSavePayload(freshOccupantInfo, formValues, {
      isEditHouseholdMemberDatesEnabled,
      // Picks buildSavePayload's applicant-shaped create branch (D5) --
      // see its own top-of-function comment.
      isApplicantSubject,
      // Only read by that same create branch, to resend the rest of the
      // household alongside the new member -- see buildSavePayload.js's
      // getExistingApplicantAdults for why that's required.
      applicants,
      // Lets the payload tell "left blank" from "section never rendered".
      isAffordable,
      isCommercial,
    });
    // ENG-30200 AC#3/#4 -- no id yet means this is a genuine create, not an
    // update; buildMiniProfileSavePayload's own occupantInfo?.id check
    // (buildSavePayload.js) is what picked the create-payload shape above,
    // this mirrors that same check to pick the matching mutation.
    if (!freshOccupantInfo?.id) {
      const createHouseholdMember = () => {
        if (isApplicantSubject) {
          createApplicantMutation.mutate(payload);
        } else {
          createMutation.mutate(payload);
        }
      };
      // Adding a member reopens a completed certification, so it is
      // confirmed first -- applicant path only. The resident Add flow has
      // never had a confirmation step and must not gain one.
      if (isApplicantSubject && willReopenCertification) {
        setPendingCreatePayload(payload);
        return;
      }
      createHouseholdMember();
      return;
    }
    // A Pending member (adult or minor -- see useMiniProfileSubject.
    // helpers.js) is always saved via applicantMutation, on both subjects:
    // it's already residentId-free (takes applicantId + isAdult), and an
    // applicant-subject member is always Pending, so this branch is
    // reached identically either way.
    if (freshOccupantInfo?.status === 'Pending') {
      applicantMutation.mutate(payload);
    } else {
      residentMutation.mutate(payload);
    }
  };

  // Rendered outside the loading gate below, and as a sibling of the
  // Mini-Profile rather than inside it: Delete closes the Mini-Profile for
  // real, so the confirmation has to outlive it.
  const closeAddConfirm = () => setPendingCreatePayload(null);
  // Rendered only while open: an always-mounted second ModalConfirm would
  // sit in the tree for every other flow that has nothing to confirm.
  const addConfirmModal = !pendingCreatePayload ? null : (
    <ModalConfirm
      open={Boolean(pendingCreatePayload)}
      onClose={closeAddConfirm}
      title={formatMessage(bridgeMessages.addHouseholdMemberConfirmTitle)}
      // Same width as the delete confirmation (ENG-30354) and the design.
      // Height stays content-driven -- the reopen banner makes the body grow.
      ModalProps={{
        PaperProps: {
          sx: { width: { xs: 'calc(100% - 32px)', sm: 565 } },
        },
      }}
      actionsProps={[
        {
          children: formatMessage(appMessages.cancel),
          variant: 'text',
          onClick: closeAddConfirm,
          'data-testid': 'mini-profile-add-cancel-button',
        },
        {
          children: formatMessage(appMessages.confirm),
          disabled: createApplicantMutation.isLoading,
          onClick: () => {
            createApplicantMutation.mutate(pendingCreatePayload);
            closeAddConfirm();
          },
          'data-testid': 'mini-profile-add-confirm-button',
        },
      ]}
    >
      <Stack spacing={2}>
        <Typography variant="bodyMedium">
          {formatMessage(bridgeMessages.addHouseholdMemberConfirmMessage)}
        </Typography>
        <Banner
          status="warning"
          message={formatMessage(
            editApplicationHouseholdMessages.confirmHouseholdEditWithCompletedCertifications,
            { br: ' ' },
          )}
        />
      </Stack>
    </ModalConfirm>
  );

  const deleteConfirmModal = (
    <ModalConfirm
      open={openConfirm}
      onClose={closeConfirmModal}
      title={formatMessage(bridgeMessages.deleteHouseholdMemberConfirmTitle)}
      // Width from the design; height stays content-driven, since the body
      // grows with the member's own copy.
      ModalProps={{
        PaperProps: {
          sx: { width: { xs: 'calc(100% - 32px)', sm: 565 } },
        },
      }}
      actionsProps={[
        {
          children: formatMessage(appMessages.cancel),
          variant: 'text',
          onClick: closeConfirmModal,
          'data-testid': 'mini-profile-delete-cancel-button',
        },
        {
          children: formatMessage(appMessages.confirm),
          disabled:
            deleteMutation.isLoading || deleteApplicantMutation.isLoading,
          onClick: handleDeleteConfirm,
          'data-testid': 'mini-profile-delete-confirm-button',
        },
      ]}
    >
      <Stack spacing={2}>
        {/* ModalConfirm's own props are a Pick that drops `status`/`icon`, so
            the error styling can only come from a Banner in the body. */}
        <Banner
          status="error"
          message={formatMessage(
            bridgeMessages.deleteHouseholdMemberConfirmBanner,
          )}
        />
        <Typography variant="bodyMedium">
          {formatMessage(bridgeMessages.deleteHouseholdMemberConfirmMessage)}
        </Typography>
        {/* A delete counts toward haveHouseholdMembersChanged same as an add
            -- same reopen warning the Add confirmation shows, under the same
            condition. */}
        {isApplicantSubject && willReopenCertification ? (
          <Banner
            status="warning"
            message={formatMessage(
              editApplicationHouseholdMessages.confirmHouseholdEditWithCompletedCertifications,
              { br: ' ' },
            )}
          />
        ) : null}
      </Stack>
    </ModalConfirm>
  );

  if (isLoadingFreshOccupantInfo) {
    return (
      <>
        {open ? <AsyncBox loading sx={{ height: 400 }} /> : null}
        {deleteConfirmModal}
        {addConfirmModal}
      </>
    );
  }

  return (
    <>
      <MiniProfileBridgeContent
        addConfirmationOpen={Boolean(pendingCreatePayload)}
        // Keyed on the ORIGINAL occupantInfo prop, not freshOccupantInfo:
        // react-hook-form's defaultValues (inside useSectionedModal) are only
        // read on this component's first mount (see its own top-of-file
        // comment), so remounting must happen exactly when a different member
        // is selected, and never mid-edit.
        key={occupantInfo?.id ?? 'new'}
        open={open}
        onClose={onClose}
        title={title}
        occupantInfo={freshOccupantInfo}
        organizationId={organizationId}
        propertyId={propertyId}
        residentId={residentId}
        isResidentPortalActive={selectedProperty?.isResidentPortalActive}
        applicationId={subject?.applicationId ?? null}
        isAffordaPortal={isAffordaPortal}
        onPortalActionComplete={onSaveSuccess}
        suffixes={suffixes}
        relationshipOptions={relationshipOptions}
        minorRelationshipOptions={minorRelationshipOptions}
        applicantTypeOptions={applicantTypeOptions}
        agedUpMinorApplicantTypeId={agedUpMinorApplicantTypeId}
        guarantorOptions={guarantorOptions}
        liveInCaretakerOptions={liveInCaretakerOptions}
        states={states}
        countries={countries}
        affordableRelationships={affordableRelationships}
        specialNeedsDesignations={specialNeedsDesignations}
        isAffordable={isAffordable}
        isCommercial={isCommercial}
        isApplicantSubject={isApplicantSubject}
        householdHasOpenCerts={Boolean(householdHasOpenCerts)}
        hasLockedOpenCert={hasLockedOpenCert}
        lockInfoPending={lockInfoPending}
        isPriorResident={Boolean(isPriorResident)}
        householdMoveInDate={householdMoveInDate}
        householdMoveOutDate={householdMoveOutDate}
        annualIncome={annualIncome}
        onSave={handleSave}
        isSaving={
          residentMutation.isLoading ||
          applicantMutation.isLoading ||
          createMutation.isLoading ||
          createApplicantMutation.isLoading
        }
        isFetchingFreshOccupantInfo={isFetchingFreshOccupantInfo}
        isEditHouseholdMemberDatesEnabled={isEditHouseholdMemberDatesEnabled}
        canDeleteHouseholdMember={canDeleteHouseholdMember}
        canEditStartEndDates={canEditStartEndDates}
        onDeleteHouseholdMember={handleDeleteClick}
        deleteBlockedReason={deleteEligibility?.blockedReason ?? null}
        isDeleteEligibilityUnknown={isDeleteEligibilityUnknown}
      />
      {deleteConfirmModal}
      {addConfirmModal}
    </>
  );
};

export default MiniProfileBridge;
